import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigation } from "@react-navigation/native";
import { useGetProductById, useReportBadMedia } from "@apis/diamondDetail";
import { getProductPrefix, ProductCategory } from "@utils/constants";
import { IncludedCategories } from "@apis/utils/productUtils";
import {
  isDiamondOrLabGrown,
  isGemstone,
  isJewelry,
} from "@utils/product/productValidators";
import { usePdpSimilarProducts } from "./usePdpSimilarProducts";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { Diamond } from "@src/types/DiamondDetail";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useDispatch, useSelector } from "react-redux";
import { setBuilderProduct } from "@redux/slices/appStateSlice";
import {
  BreadCrumbLabel,
  NavigationHistoryState,
} from "@components/molecules/Breadcrumbs/utils";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import Routes from "@navigations/Routes";
import { navigationRef } from "@navigations/utils/RootNavigation";
import { CommonActions } from "@react-navigation/native";

const isJewelryCategory = (type?: string) =>
  type === ProductCategory.JEWELRY || type === ProductCategory.LABGROWN_JEWELRY;

const resolvePdpItemDetail = ({
  isModal,
  fromComponent,
  localComponentDetail,
  productID,
  matchingPairItemDetail,
  mStoneItemDetail,
  mItem,
  productType,
}: {
  isModal: boolean;
  fromComponent?: boolean;
  localComponentDetail: any;
  productID?: number | string;
  matchingPairItemDetail: any;
  mStoneItemDetail: Diamond;
  mItem: Diamond;
  productType: ProductCategory;
}): Diamond => {
  if (isModal) {
    if (fromComponent && localComponentDetail) {
      return localComponentDetail;
    }
    if (productID === matchingPairItemDetail?.id) {
      return matchingPairItemDetail;
    }
    return mStoneItemDetail?.type !== ProductCategory.JEWELRY
      ? mStoneItemDetail
      : mItem;
  }

  if (
    isJewelryCategory(productType) &&
    mItem?.type &&
    !isJewelryCategory(mItem.type)
  ) {
    if (isJewelryCategory(mStoneItemDetail?.type)) {
      return mStoneItemDetail;
    }
    return {} as Diamond;
  }

  return mItem;
};

interface UsePdpProductDataProps {
  route: any;
  isFocused: boolean;
  isModal: boolean;
  product_type: ProductCategory;
  priceMode: number;
  priceMarkup: number;
  userInfo: any;
  mGroupIds: any;
  isCustomApp: boolean;
  additionalAttributes?: any;
  componentData?: any;
  findMatchSectionLayout: any;
  mItem: Diamond;
  mStoneItemDetail: Diamond;
  matchingPairItemDetail: any;
  setItemDetail: (item: Diamond) => void;
  setStoneItemDetail: (item: any) => void;
  setMatchingPairItemDetail: (item: any) => void;
  setIsVariationRequired: (required: boolean) => void;
  setDefaultSlug: (slug: any) => void;
  setPersonalization: (personalization: any) => void;
  jcSelectedOption?: any;
  appendNewVariation?: (variations: any[]) => void;
  setAllTypes?: (item: any) => void;
  renderShowErrorComponent: () => void;
  showLabGrownPopUp: () => void;
  fromReviewPage: boolean;
  enterSecondFlow: boolean;
  resetDynamicParams?: () => void;
  applyFetchedItemPrice?: (source?: Diamond) => void;
}

export const usePdpProductData = ({
  route,
  isFocused,
  isModal,
  product_type,
  priceMode,
  priceMarkup,
  userInfo,
  mGroupIds,
  isCustomApp,
  additionalAttributes,
  componentData,
  findMatchSectionLayout,
  mItem,
  mStoneItemDetail,
  matchingPairItemDetail,
  setItemDetail,
  setStoneItemDetail,
  setMatchingPairItemDetail,
  setIsVariationRequired,
  setDefaultSlug,
  setPersonalization,
  jcSelectedOption,
  appendNewVariation,
  setAllTypes,
  renderShowErrorComponent,
  showLabGrownPopUp,
  fromReviewPage,
  enterSecondFlow,
  resetDynamicParams,
  applyFetchedItemPrice,
}: UsePdpProductDataProps) => {
  const navigation = useNavigation();

  const [localComponentDetail, setLocalComponentDetail] = useState<any>(null);
  const { addRouteToHistory, clearRouteHistory } = useNavigationHistory();
  const productPrefix = useProductPrefix();
  const dispatch = useDispatch();
  const { theme } = useScreenOptionsHooks();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );

  const { mutate: reportBadMediaAPI } = useReportBadMedia();

  const processedDataRef = useRef<string>("");
  const lastProductIdRef = useRef<number | string | null>(null);
  const lastNavigationRequestIdRef = useRef<number | null>(null);
  // Route id whose product has already been copied into configurator context.
  // A later context id is a variation chosen on this page. A different route
  // id means Find a Match opened another product, so this sync is cleared.
  const syncedRouteProductIdRef = useRef<number | null>(null);
  const { enableJewelryBuilder } = useOrgSettingsSlice();

  const productId = useMemo(() => {
    return route?.params?.type === "stock"
      ? route?.params?.productID
      : Number(route?.params?.productID);
  }, [route?.params?.type, route?.params?.productID]);

  const initialItemDetail: Diamond = useMemo(() => {
    return resolvePdpItemDetail({
      isModal,
      fromComponent: route?.params?.fromComponent,
      localComponentDetail,
      productID: route?.params?.productID,
      matchingPairItemDetail,
      mStoneItemDetail,
      mItem,
      productType: product_type,
    });
  }, [
    isModal,
    route?.params?.fromComponent,
    route?.params?.productID,
    localComponentDetail,
    matchingPairItemDetail,
    mStoneItemDetail,
    mItem,
    product_type,
  ]);

  // Main product data fetching
  const getDiamondDetailData = useGetProductById(
    isFocused && !componentData,
    product_type as IncludedCategories,
    productId,
    userInfo,
    priceMode,
    priceMarkup,
    product_type,
    mGroupIds,
    isCustomApp,
    additionalAttributes,
    route?.params?.type ?? null
  );

  // Similar products/match pair data fetching
  const { getMatchPairDetailData } = usePdpSimilarProducts({
    route,
    isModal,
    product_type,
    priceMode,
    priceMarkup,
    userInfo,
    mGroupIds,
    findMatchSectionLayout,
  });

  const contextItemDetail = useMemo<Diamond>(() => {
    return resolvePdpItemDetail({
      isModal,
      fromComponent: route?.params?.fromComponent,
      localComponentDetail,
      productID: route?.params?.productID,
      matchingPairItemDetail,
      mStoneItemDetail,
      mItem,
      productType: product_type,
    });
  }, [
    isModal,
    route?.params?.fromComponent,
    route?.params?.productID,
    localComponentDetail,
    matchingPairItemDetail,
    mStoneItemDetail,
    mItem,
    product_type,
  ]);

  const routeProductId = Number(productId);
  const contextProductId =
    contextItemDetail?.id != null ? Number(contextItemDetail.id) : null;
  if (contextProductId != null && contextProductId === routeProductId) {
    syncedRouteProductIdRef.current = routeProductId;
  } else if (
    syncedRouteProductIdRef.current != null &&
    syncedRouteProductIdRef.current !== routeProductId
  ) {
    syncedRouteProductIdRef.current = null;
  }
  const contextIsSelectedVariation =
    isJewelry(product_type) &&
    isJewelry(contextItemDetail?.type) &&
    contextProductId != null &&
    contextProductId !== routeProductId &&
    syncedRouteProductIdRef.current === routeProductId;

  const storedSettingVariation =
    isJewelryCategory(product_type) &&
    isJewelryCategory(mStoneItemDetail?.type) &&
    mStoneItemDetail?.id != null &&
    Number(mStoneItemDetail.id) !== routeProductId &&
    syncedRouteProductIdRef.current === routeProductId;

  // Find a Match "More Details" pops back onto this screen after clearing the
  // configurator item. On iPad the screen is frozen while that query result
  // arrives, so the effect that copies it into context often never flushes and
  // the page stays on the loader. Read the fetched product directly when the
  // context item is for a different product. A jewelry variation selected on
  // this page keeps its own id, so that selection still has to be shown.
  const itemDetail = useMemo<Diamond>(() => {
    const contextMatchesProduct =
      contextItemDetail?.id != null &&
      Number(contextItemDetail.id) === Number(productId) &&
      (!product_type || contextItemDetail.type === product_type);

    if (isModal || contextMatchesProduct || contextIsSelectedVariation) {
      return contextItemDetail;
    }

    if (storedSettingVariation) {
      return mStoneItemDetail;
    }

    const body = getDiamondDetailData.data?.data?.response?.body;
    const fetchedProduct =
      body?.jewelries?.[0] || body?.diamonds?.[0] || body?.gemstones?.[0];

    if (
      fetchedProduct?.id != null &&
      Number(fetchedProduct.id) === Number(productId)
    ) {
      if (isJewelry(product_type)) {
        return {
          ...fetchedProduct,
          variation_required: body?.variation_required || false,
        };
      }
      return fetchedProduct;
    }

    const preloadedItem = route?.params?.preloadedItem;
    if (
      preloadedItem?.id != null &&
      Number(preloadedItem.id) === Number(productId)
    ) {
      return preloadedItem;
    }

    return contextItemDetail;
  }, [
    isModal,
    contextItemDetail,
    contextIsSelectedVariation,
    storedSettingVariation,
    mStoneItemDetail,
    productId,
    product_type,
    getDiamondDetailData.data,
    route?.params?.preloadedItem,
  ]);

  useEffect(() => {
    const navigationRequestId = Number(route?.params?.navigationRequestId || 0);
    if (!isFocused || !navigationRequestId) {
      return;
    }
    if (lastNavigationRequestIdRef.current === navigationRequestId) {
      return;
    }

    lastNavigationRequestIdRef.current = navigationRequestId;
    processedDataRef.current = "";
    lastProductIdRef.current = null;
    getDiamondDetailData.refetch?.().then();
  }, [isFocused, route?.params?.navigationRequestId]);

  useEffect(() => {
    if (componentData && isFocused && isModal && route?.params?.fromComponent) {
      setLocalComponentDetail(componentData);
    }
  }, [componentData, isFocused, isModal, route?.params?.fromComponent]);

  // Error handling effect
  useEffect(() => {
    if (getDiamondDetailData?.isError) {
      renderShowErrorComponent();
    }
  }, [getDiamondDetailData?.isError]);

  // Reset processed refs when variation config changes
  useEffect(() => {
    const configKey = JSON.stringify(additionalAttributes?.config || {});
    if (
      processedDataRef.current &&
      !processedDataRef.current.endsWith(`-${configKey}`)
    ) {
      processedDataRef.current = "";
    }
  }, [additionalAttributes?.config]);

  useEffect(() => {
    if (isFocused) {
      processedDataRef.current = "";
      lastProductIdRef.current = null;
    }
  }, [isFocused, productId, priceMode, userInfo.user?.locale]);

  const addToHistory = () => {
    let mIndex = -1;
    if (history.length > 0) {
      const mRoutes = history.map((scr) => ({
        name: scr.screen,
        params: scr.params,
      }));

      mIndex = mRoutes.findIndex(
        (s) =>
          s.name === Routes.PDPScreen && s.params?.productType === product_type
      );
    }
    if (
      enableJewelryBuilder &&
      (theme.isLargeScreen || theme.isTablet) &&
      mIndex === -1
    ) {
      const name = Routes.PDPScreen;
      const params = {
        productID: Number(itemDetail?.id),
        productType: product_type,
        priceMode: Number(priceMode),
        productPrefix: productPrefix,
        breadCrumbLabel:
          product_type === ProductCategory.JEWELRY
            ? BreadCrumbLabel.SETTING_DETAILS
            : BreadCrumbLabel.STONE_DETAILS,
      };
      const xRoute = { name, params };
      addRouteToHistory(xRoute);
      dispatch(setBuilderProduct(product_type));
    }
  };

  const removeBreadCrumbFromHistory = useCallback(() => {
    if (enableJewelryBuilder) {
      let mIndex = -1;
      if (history.length === 1) {
        const mRoutes = history.map((scr) => ({
          name: scr.screen,
          params: scr.params,
        }));

        mIndex = mRoutes.findIndex(
          (s) =>
            s.name === Routes.PDPScreen &&
            s.params?.productType === product_type
        );
        if (mIndex === 0) {
          clearRouteHistory();
        }
      }
    }
  }, [enableJewelryBuilder, history, product_type]);

  useEffect(() => {
    if (enableJewelryBuilder && isFocused) {
      if (enterSecondFlow && history.length < 2) {
        const productType = userInfo.defaultStockItemType();
        const targetScreen =
          theme.isTablet || theme.isLargeScreen
            ? Routes.FilterSplitScreen
            : Routes.SearchResultScreen;
        if (navigationRef.isReady()) {
          navigationRef.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: Routes.AppNavigator,
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: Routes.ExploreNavigator,
                        state: {
                          index: 0,
                          routes: [
                            {
                              name: targetScreen,
                              params: {
                                productType: productType,
                                productPrefix: getProductPrefix(
                                  productType as ProductCategory
                                ),
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            })
          );
        }
        return;
      }
      addToHistory();
      if (!isFocused || isModal) {
        return;
      }

      if (!productId || !mItem) {
        return;
      }
      const isDifferentProduct =
        Number(mItem?.id) !== Number(productId) || mItem?.type !== product_type;
      if (!isDifferentProduct) {
        return;
      }

      const isJewelryPdp = isJewelryCategory(product_type);
      if (!isJewelryPdp) {
        const body = getDiamondDetailData.data?.data?.response?.body;
        const fetchedStone = body?.diamonds?.[0] || body?.gemstones?.[0];
        const preloadedItem = route?.params?.preloadedItem;
        // More Details pops back onto this frozen screen after Find a Match
        // cleared the item. Keep the stone that was just opened, including
        // the matching stone, instead of clearing it and waiting on a fetch.
        if (
          fetchedStone?.id != null &&
          Number(fetchedStone.id) === Number(productId)
        ) {
          setItemDetail(fetchedStone);
        } else if (
          preloadedItem?.id != null &&
          Number(preloadedItem.id) === Number(productId)
        ) {
          setItemDetail(preloadedItem);
        } else if (Number(mItem?.id) === Number(productId)) {
          return;
        } else {
          setItemDetail({} as any);
        }
        return;
      }

      if (!isJewelryCategory(mItem?.type)) {
        // Setting PDP after setting-to-stone: restore the setting stored in
        // stoneItemDetail instead of flashing the selected stone.
        if (isJewelryCategory(mStoneItemDetail?.type)) {
          setItemDetail(mStoneItemDetail);
        } else {
          setItemDetail({} as any);
        }
      }
    }
  }, [isFocused]);
  // Track previous isFocused to detect when screen comes back into focus
  const prevIsFocusedRef = useRef<boolean>(isFocused);
  useEffect(() => {
    // If screen just came into focus (was false, now true), reset processed refs
    if (!prevIsFocusedRef.current && isFocused) {
      processedDataRef.current = "";
      lastProductIdRef.current = null;
    }
    prevIsFocusedRef.current = isFocused;
  }, [isFocused]);

  useEffect(() => {
    if (
      getDiamondDetailData?.isSuccess &&
      getDiamondDetailData?.isFetched &&
      !getDiamondDetailData?.isFetching &&
      isFocused
    ) {
      const data = getDiamondDetailData.data?.data;

      const detailItem =
        data?.response?.body?.jewelries?.[0] ||
        data?.response?.body?.diamonds?.[0] ||
        data?.response?.body?.gemstones?.[0];
      const dataKey = detailItem?.id;
      const configKey = JSON.stringify(additionalAttributes?.config || {});
      const priceKey =
        detailItem?.updated_total_sales_price ?? detailItem?.total_sales_price;
      const uniqueKey = `${dataKey}-${configKey}-${priceMode}-${
        priceKey ?? ""
      }`;

      if (processedDataRef.current === uniqueKey) {
        return;
      }

      processedDataRef.current = uniqueKey;

      showLabGrownPopUp();

      if (isDiamondOrLabGrown(product_type) && isFocused) {
        if (isModal && route?.params?.fromComponent) {
          setLocalComponentDetail(data.response.body?.diamonds[0]);
        } else if (isModal) {
          if (mItem?.type === ProductCategory.DIAMOND) {
            setItemDetail(data.response.body?.diamonds[0]);
          } else {
            if (mStoneItemDetail?.id === route?.params?.productID) {
              setStoneItemDetail(data.response.body?.diamonds[0]);
            } else if (!fromReviewPage && data.response.body?.diamonds[0]) {
              setMatchingPairItemDetail(data.response.body?.diamonds[0]);
            }
          }
        } else {
          setItemDetail(data.response.body?.diamonds[0]);
        }
        if (data.response.body?.diamonds.length === 0) {
          renderShowErrorComponent();
        }
      } else if (isGemstone(product_type) && isFocused) {
        if (isModal && route?.params?.fromComponent) {
          setLocalComponentDetail(data.response.body?.gemstones[0]);
        } else if (isModal) {
          if (mItem?.type === ProductCategory.GEMSTONE) {
            setItemDetail(data.response.body?.gemstones[0]);
          } else {
            if (mStoneItemDetail?.id === route?.params?.productID) {
              setStoneItemDetail(data.response.body?.gemstones[0]);
            } else if (!fromReviewPage && data.response.body?.gemstones[0]) {
              setMatchingPairItemDetail(data.response.body?.gemstones[0]);
            }
          }
        } else {
          setItemDetail(data.response.body?.gemstones[0]);
        }
        if (data.response.body?.gemstones.length === 0) {
          renderShowErrorComponent();
        }
      } else if (isJewelry(product_type) && isFocused) {
        if (isModal && route?.params?.fromComponent) {
          setLocalComponentDetail({
            ...data.response.body?.jewelries[0],
            variation_required: data.response.body?.variation_required || false,
          });
        } else if (!isModal) {
          const newId = Number(data.response.body?.jewelries[0]?.id);
          const hasUserConfiguration =
            Object.keys(jcSelectedOption?.current?.option_value || {}).length >
            0;
          // Coming back from stone search refetches this same setting. Applying
          // that payload again clears the finger size the user already chose,
          // and can also drop the required-field flags so they can continue
          // with the size still empty.
          // A metal change stores the new variation under a different id while
          // the route stays on the opened item. Do not write the original
          // fetch back over that selection. A new route id clears the sync, so
          // Find a Match still applies the product that was just opened.
          const routeStillOnOpenedSetting =
            syncedRouteProductIdRef.current === Number(productId);
          const selectedVariationDiffersFromFetch =
            routeStillOnOpenedSetting &&
            ((isJewelryCategory(mItem?.type) &&
              mItem?.id != null &&
              Number(mItem.id) !== Number(productId) &&
              Number(mItem.id) !== newId) ||
              (isJewelryCategory(mStoneItemDetail?.type) &&
                mStoneItemDetail?.id != null &&
                Number(mStoneItemDetail.id) !== Number(productId) &&
                Number(mStoneItemDetail.id) !== newId));

          // Coming back from stone search refetches this same setting. Applying
          // that payload again clears the finger size the user already chose,
          // and can also drop the required-field flags so they can continue
          // with the size still empty. A price-mode change still has to copy
          // the new total onto the item already on screen.
          const sameConfiguredItem =
            isJewelryCategory(mItem?.type) &&
            Number(mItem?.id) === newId &&
            hasUserConfiguration;
          if (sameConfiguredItem) {
            applyFetchedItemPrice?.(data.response.body?.jewelries[0]);
            return;
          }
          if (selectedVariationDiffersFromFetch) {
            return;
          }
          // Only update params if productID actually changed to prevent loops
          if (
            (route?.params?.productID !== newId || route?.params?.type) &&
            lastProductIdRef.current !== newId
          ) {
            lastProductIdRef.current = newId;
            navigation.setParams({
              ...route.params,
              productID: newId,
              type: undefined,
            });
          }
          // A brand-new product page is loading. Clear any variation slug
          // filters left over from a previously opened configurable item so
          // they don't leak into this item's variation fetch and trigger a
          // spurious "combination not available" toast.
          resetDynamicParams?.();
          setItemDetail({
            ...data.response.body?.jewelries[0],
            variation_required: data.response.body?.variation_required || false,
          });
          setIsVariationRequired(data.response.body?.variation_required);
          appendNewVariation?.(data.response.body?.variations ?? []);
          if (data.response.body?.variation_required) {
            setAllTypes?.(data.response.body?.jewelries[0]);
          }
          setDefaultSlug(data.response.body?.jewelries[0]?.slug_detail);
          if (additionalAttributes?.config && jcSelectedOption) {
            jcSelectedOption.current.option_value = additionalAttributes.config;
          }
          if (additionalAttributes?.personalization) {
            setPersonalization(additionalAttributes.personalization);
          }
        }
        if (data.response.body?.jewelries.length === 0) {
          renderShowErrorComponent();
        }
      }
    }
  }, [
    getDiamondDetailData?.data,
    getDiamondDetailData?.isSuccess,
    getDiamondDetailData?.isFetched,
    getDiamondDetailData?.isFetching,
  ]);

  const reportBadMedia = (media_id: number) => {
    if (media_id) {
      reportBadMediaAPI({ media_id });
    }
  };

  return {
    getDiamondDetailData,
    getMatchPairDetailData,
    productId,
    itemDetail,
    initialItemDetail,
    localComponentDetail,
    setLocalComponentDetail,
    reportBadMedia,
    removeBreadCrumbFromHistory,
  };
};
